import { Box, Chip, Typography, alpha, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import dayjs from 'dayjs';

type DropdownOption = { label: string; value: string | number };

export type ReviewTabSummaryProps = {
  formData: Record<string, any>;
  eventTypeList: DropdownOption[];
  companyMasters: DropdownOption[];
  isCorporate: boolean;
  isComplementary?: boolean;
  eventCount?: number;
  roomCount?: number;
};

export const resolveId = (value: unknown): string | number | undefined => {
  if (value == null || value === '') return undefined;
  if (typeof value === 'object' && value !== null && 'value' in (value as object)) {
    return (value as { value: string | number }).value;
  }
  return value as string | number;
};

export const labelForId = (id: unknown, options: DropdownOption[]): string => {
  const resolved = resolveId(id);
  if (resolved == null) return '';
  return options.find(o => String(o.value) === String(resolved))?.label?.trim() ?? '';
};

/** e.g. 27–28 Aug 2026 */
export const formatBookingDateRange = (startRaw: unknown, endRaw: unknown): string => {
  const start = startRaw ? dayjs(startRaw as string) : null;
  const end = endRaw ? dayjs(endRaw as string) : null;
  if (!start?.isValid() && !end?.isValid()) return '';
  if (start?.isValid() && (!end?.isValid() || start.isSame(end, 'day'))) {
    return start.format('D MMM YYYY');
  }
  if (start?.isValid() && end?.isValid()) {
    if (start.isSame(end, 'month') && start.isSame(end, 'year')) {
      return `${start.format('D')}–${end.format('D MMM YYYY')}`;
    }
    return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
  }
  return (start?.isValid() ? start : end!)!.format('D MMM YYYY');
};

export const buildCustomerName = (
  formData: Record<string, any>,
  isCorporate: boolean,
  companyMasters: DropdownOption[],
): string => {
  if (isCorporate) {
    return labelForId(formData.companyMaster, companyMasters) || '—';
  }
  const firstRaw = formData.firstName;
  const first =
    typeof firstRaw === 'object' && firstRaw !== null && 'firstName' in firstRaw
      ? String((firstRaw as { firstName?: string }).firstName ?? '').trim()
      : String(firstRaw ?? '').trim();
  const last = String(formData.lastName ?? '').trim();
  const name = [first, last].filter(Boolean).join(' ');
  return name || '—';
};

const formatCountLabel = (count: number, singular: string, pluralTemplate: string): string => {
  if (count === 1) return singular;
  return pluralTemplate.replace('{count}', String(count));
};

export const ReviewTabSummary = ({
  formData,
  eventTypeList,
  companyMasters,
  isCorporate,
  isComplementary = false,
  eventCount = 0,
  roomCount = 0,
}: ReviewTabSummaryProps) => {
  const theme = useTheme();

  const customerName = buildCustomerName(formData, isCorporate, companyMasters);
  const bookingTypeLabel = isCorporate
    ? strings.customermgmt_text_Company
    : strings.customermgmt_text_individual;
  const eventTypeLabel = labelForId(formData.eventType, eventTypeList);
  const dateRangeLabel = formatBookingDateRange(formData.startDate, formData.endDate);

  const metaLine = [bookingTypeLabel, eventTypeLabel].filter(Boolean).join(' · ');

  const countParts = [
    eventCount > 0
      ? formatCountLabel(
          eventCount,
          strings.new_booking_review_event_count_singular,
          strings.new_booking_review_event_count,
        )
      : '',
    roomCount > 0
      ? formatCountLabel(
          roomCount,
          strings.new_booking_review_room_count_singular,
          strings.new_booking_review_room_count,
        )
      : '',
  ].filter(Boolean);

  const countsLine = countParts.join(' · ');

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1.5,
        border: `1px dashed ${alpha(theme.palette.divider, 0.55)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {customerName}
        </Typography>
        {isComplementary ? (
          <Chip
            label={strings.bookingDetail_badge_complementary}
            size="small"
            sx={{ bgcolor: '#e0f7fa', color: '#00838f', fontWeight: 600, flexShrink: 0 }}
          />
        ) : null}
      </Box>

      {metaLine && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
          {metaLine}
        </Typography>
      )}

      {dateRangeLabel && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: countsLine ? 0.25 : 0 }}>
          {dateRangeLabel}
        </Typography>
      )}

      {countsLine ? (
        <Typography variant="body2" color="text.secondary">
          {countsLine}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ReviewTabSummary;
